'use client';
import {
  type ComponentProps as ReactComponentProps,
  Suspense,
  memo,
  createElement,
} from 'react';

import { type VariantProps } from 'tailwind-variants';

import { useIconContext } from '../../../context/IconContext';
import { coreIcons } from '../../../core-icon/icons';
import { ErrorBoundary } from '../../../error-boundary/ErrorBoundary';
import { type Component, type ComponentProps } from '../../types/component';
import { tv } from '../../utils/tv';

const coreIconClasses = {
  root: 'zds-coreicon-root',
};

type Variant = VariantProps<typeof iconStyles>;
interface CoreIconBaseProps {
  /**
   * The name of the icon.
   */
  name: keyof typeof coreIcons;
  /**
   * Icon fill color. If no color given, default to inherit.
   * @default 'inherit'
   */
  color?: Variant['color'];
  /**
   * Icon `width` and `height`.
   * @default 'md'
   */
  size?: Variant['size'];
  /**
   * Called if an exception is thrown during loading.
   */
  onError?: ReactComponentProps<typeof ErrorBoundary>['onError'];
}

type CoreIconProps = ComponentProps<CoreIconBaseProps, 'svg'>;

type CoreIconComponent = Component<CoreIconBaseProps, 'svg'>;

const iconStyles = tv({
  base: [coreIconClasses.root],
  variants: {
    size: {
      xs: 'z--icon-xs-height z--icon-xs-width',
      sm: 'z--icon-sm-height z--icon-sm-width',
      md: 'z--icon-md-height z--icon-md-width',
      lg: 'z--icon-lg-height z--icon-lg-width',
      inherit: 'h-[1em] w-[1em]',
    },
    color: {
      inherit: 'text-inherit',
      primary: `text-accent-primary-main`,
      secondary: `text-accent-secondary-main`,
      success: `text-success-main`,
      error: `text-error-main`,
      warning: `text-warning-main`,
      info: `text-info-main`,
      disabled: `disabled-foreground`,
      white: 'text-static-white-main',
    },
  },
});

const FallbackIcon = ({
  className,
  ...props
}: Omit<CoreIconProps, 'name' | 'onError' | 'color' | 'size'>) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width={24}
    height={24}
    fill="none"
    viewBox="0 0 24 24"
    className={className}
    aria-hidden={true}
    data-testid="fallback-icon"
  />
);

// The memoisation techniques used here are to prevent unnecessary re-renders
// of the Suspense boundary and allow the passing of non-primitive props
// https://serko.atlassian.net/browse/ZDS-1370

const CoreIcon = memo(
  ({
    name,
    color = 'inherit',
    size = 'md',
    ref,
    className,
    ...props
  }: CoreIconProps) => {
    const iconContext = useIconContext();
    const IconComponent = iconContext.icons
      ? iconContext.icons?.[name]
      : coreIcons[name];

    const styles = iconStyles({
      color,
      size,
      className,
    });

    const InnerCoreIcon = memo(
      ({
        ref: innerRef,
        onError,
        className: innerCoreClassName,
        ...innerCoreProps
      }: Omit<CoreIconProps, 'color' | 'name' | 'size'>) => {
        return (
          <ErrorBoundary
            fallback={
              <FallbackIcon
                {...innerCoreProps}
                className={innerCoreClassName}
              />
            }
            onError={onError}
          >
            <Suspense
              fallback={
                <FallbackIcon
                  {...innerCoreProps}
                  className={innerCoreClassName}
                />
              }
            >
              {IconComponent ? (
                <IconComponent
                  {...innerCoreProps}
                  className={innerCoreClassName}
                  ref={innerRef}
                  aria-hidden={true}
                />
              ) : (
                <FallbackIcon
                  {...innerCoreProps}
                  className={innerCoreClassName}
                />
              )}
            </Suspense>
          </ErrorBoundary>
        );
      }
    );

    // We use createElement instead of JSX syntax (<InnerCoreIcon {...props} />) for a critical reason:
    // When using createElement, React treats InnerCoreIcon as a component type rather than evaluating
    // it as a function component on each render. This preserves the memoization of InnerCoreIcon even when
    // the parent re-renders with new object props (like event handlers or onError callbacks).
    // Without this pattern, new object props would cause unnecessary re-renders, recreating Suspense
    // boundaries and causing performance issues when many icons are present on a page.

    // https://github.com/facebook/react/issues/31357
    // eslint-disable-next-line react-compiler/react-compiler
    return createElement(InnerCoreIcon, {
      ...props,
      className: styles,
      ref,
    });
  }
) as CoreIconComponent;

CoreIcon.displayName = 'CoreIcon';

export {
  CoreIcon,
  type CoreIconProps,
  type CoreIconBaseProps,
  coreIconClasses,
};
